import { i18n } from "@/plugin/vue-i18n";
import type { RuleResult } from "./types";

/**
 * Per-rule copy, resolved through literal locale keys.
 *
 * The repo forbids dynamic i18n keys and a rule's id is only known at runtime,
 * so every key is spelled out at its call site rather than built from a
 * template string. en.json stays the single source of the strings themselves,
 * which is what the later translation pass will pick up.
 *
 * Three strings per error: the sentence for the popover, the terse marker for
 * the canvas, and the verb for the action link.
 */
type Params = Record<string, string | number> | undefined;

interface RuleCopy {
  message: (params: Params) => string;
  inline: () => string;
  action: (params: Params) => string;
}

const ERROR_COPY: Record<string, RuleCopy> = {
  custom_field_label_required: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.customFieldLabelRequired", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.customFieldLabelRequired") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.customFieldLabelRequired", params ?? {}) as string,
  },
  custom_field_name_taken: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.customFieldNameTaken", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.customFieldNameTaken") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.customFieldNameTaken", params ?? {}) as string,
  },
  custom_field_save_failed: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.customFieldSaveFailed", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.customFieldSaveFailed") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.customFieldSaveFailed", params ?? {}) as string,
  },
  url_field_domains_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.urlFieldDomainsMissing", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.urlFieldDomainsMissing") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.urlFieldDomainsMissing", params ?? {}) as string,
  },
  picture_choice_image_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.pictureChoiceImageMissing", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.pictureChoiceImageMissing") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.pictureChoiceImageMissing", params ?? {}) as string,
  },
  picture_choice_label_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.pictureChoiceLabelMissing", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.pictureChoiceLabelMissing") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.pictureChoiceLabelMissing", params ?? {}) as string,
  },
  picture_choice_duplicate_labels: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.pictureChoiceDuplicateLabels", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.pictureChoiceDuplicateLabels") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.pictureChoiceDuplicateLabels", params ?? {}) as string,
  },
  picture_choice_selection_limit_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.pictureChoiceSelectionLimitInvalid", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.pictureChoiceSelectionLimitInvalid") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.pictureChoiceSelectionLimitInvalid", params ?? {}) as string,
  },
  datetime_label_empty: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.datetimeLabelEmpty", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.datetimeLabelEmpty") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.datetimeLabelEmpty", params ?? {}) as string,
  },
  datetime_time_format_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.datetimeTimeFormatInvalid", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.datetimeTimeFormatInvalid") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.datetimeTimeFormatInvalid", params ?? {}) as string,
  },
  datetime_no_available_days: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.datetimeNoAvailableDays", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.datetimeNoAvailableDays") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.datetimeNoAvailableDays", params ?? {}) as string,
  },
  datetime_date_range_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.datetimeDateRangeInvalid", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.datetimeDateRangeInvalid") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.datetimeDateRangeInvalid", params ?? {}) as string,
  },
  datetime_time_range_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.datetimeTimeRangeInvalid", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.datetimeTimeRangeInvalid") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.datetimeTimeRangeInvalid", params ?? {}) as string,
  },
  opportunity_settings_incomplete: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.opportunitySettingsIncomplete", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.opportunitySettingsIncomplete") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.opportunitySettingsIncomplete", params ?? {}) as string,
  },
  footer_styles_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.footerStylesInvalid", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.footerStylesInvalid") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.footerStylesInvalid", params ?? {}) as string,
  },
  sell_products_no_product: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.sellProductsNoProduct", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.sellProductsNoProduct") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.sellProductsNoProduct", params ?? {}) as string,
  },
  image_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.imageMissing", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.imageMissing") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.imageMissing", params ?? {}) as string,
  },
  score_calculation_empty: {
    message: (params) =>
      i18n.global.t("builderValidation.errors.scoreCalculationEmpty", params ?? {}) as string,
    inline: () =>
      i18n.global.t("builderValidation.errorsInline.scoreCalculationEmpty") as string,
    action: (params) =>
      i18n.global.t("builderValidation.errorsAction.scoreCalculationEmpty", params ?? {}) as string,
  },
};

/** Recommendation rules carry two strings; the inline marker is errors-only. */
const RECOMMENDATION_COPY: Record<string, Omit<RuleCopy, "inline">> = {
  asset_has_no_elements: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.assetHasNoElements", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.assetHasNoElements", params ?? {}) as string,
  },
  terms_conditions_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.termsConditionsMissing", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.termsConditionsMissing", params ?? {}) as string,
  },
  captcha_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.captchaMissing", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.captchaMissing", params ?? {}) as string,
  },
  privacy_policy_url_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.privacyPolicyUrlMissing", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.privacyPolicyUrlMissing", params ?? {}) as string,
  },
  sell_products_not_live: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.sellProductsNotLive", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.sellProductsNotLive", params ?? {}) as string,
  },
  default_custom_field_name: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.defaultCustomFieldName", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.defaultCustomFieldName", params ?? {}) as string,
  },
  empty_custom_field_label_existing: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.emptyCustomFieldLabelExisting", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.emptyCustomFieldLabelExisting", params ?? {}) as string,
  },
  quiz_option_placeholder: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.quizOptionPlaceholder", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.quizOptionPlaceholder", params ?? {}) as string,
  },
  quiz_score_blank: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.quizScoreBlank", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.quizScoreBlank", params ?? {}) as string,
  },
  quiz_cta_url_placeholder: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.quizCtaUrlPlaceholder", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.quizCtaUrlPlaceholder", params ?? {}) as string,
  },
  header_text_empty: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.headerTextEmpty", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.headerTextEmpty", params ?? {}) as string,
  },
  html_element_empty: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.htmlElementEmpty", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.htmlElementEmpty", params ?? {}) as string,
  },
  button_text_empty: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.buttonTextEmpty", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.buttonTextEmpty", params ?? {}) as string,
  },
  source_field_value_empty: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.sourceFieldValueEmpty", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.sourceFieldValueEmpty", params ?? {}) as string,
  },
  option_label_empty: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.optionLabelEmpty", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.optionLabelEmpty", params ?? {}) as string,
  },
  file_upload_max_size_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.fileUploadMaxSizeInvalid", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.fileUploadMaxSizeInvalid", params ?? {}) as string,
  },
  submit_redirect_url_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.submitRedirectUrlMissing", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.submitRedirectUrlMissing", params ?? {}) as string,
  },
  submit_redirect_url_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.submitRedirectUrlInvalid", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.submitRedirectUrlInvalid", params ?? {}) as string,
  },
  disqualify_url_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.disqualifyUrlMissing", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.disqualifyUrlMissing", params ?? {}) as string,
  },
  disqualify_message_empty: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.disqualifyMessageEmpty", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.disqualifyMessageEmpty", params ?? {}) as string,
  },
  schedule_redirect_url_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.scheduleRedirectUrlInvalid", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.scheduleRedirectUrlInvalid", params ?? {}) as string,
  },
  schedule_no_closed_state_content: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.scheduleNoClosedStateContent", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.scheduleNoClosedStateContent", params ?? {}) as string,
  },
  notification_recipient_missing: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.notificationRecipientMissing", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.notificationRecipientMissing", params ?? {}) as string,
  },
  notification_email_invalid: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.notificationEmailInvalid", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.notificationEmailInvalid", params ?? {}) as string,
  },
  logic_orphan_field_reference: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.logicOrphanFieldReference", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.logicOrphanFieldReference", params ?? {}) as string,
  },
  logic_orphan_option_reference: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.logicOrphanOptionReference", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.logicOrphanOptionReference", params ?? {}) as string,
  },
  logic_orphan_target: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.logicOrphanTarget", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.logicOrphanTarget", params ?? {}) as string,
  },
  score_calculation_invalid_reference: {
    message: (params) =>
      i18n.global.t("builderValidation.recommendations.scoreCalculationInvalidReference", params ?? {}) as string,
    action: (params) =>
      i18n.global.t("builderValidation.recommendationsAction.scoreCalculationInvalidReference", params ?? {}) as string,
  },
};

/** An unmapped rule falls back to its id so a gap is visible, never blank. */
const fallback = (ruleId: string): string => ruleId;

export const errorMessage = (result: RuleResult): string =>
  ERROR_COPY[result.ruleId]?.message(result.params) ?? fallback(result.ruleId);

export const errorInline = (ruleId: string): string =>
  ERROR_COPY[ruleId]?.inline() ?? fallback(ruleId);

export const errorAction = (result: RuleResult): string =>
  ERROR_COPY[result.ruleId]?.action(result.params) ?? fallback(result.ruleId);

export const recommendationMessage = (result: RuleResult): string =>
  RECOMMENDATION_COPY[result.ruleId]?.message(result.params) ??
  fallback(result.ruleId);

export const recommendationAction = (result: RuleResult): string =>
  RECOMMENDATION_COPY[result.ruleId]?.action(result.params) ??
  fallback(result.ruleId);
